<template>
  <div>
    <div class="Content">
      <event-stage-header />
      <main class="SectionContainer u-flex">
        <section :class="[{'is-loading': ui.isLoading }, 'SectionContainer-left']">
          <dynamic-form-renderer-read-only />
        </section>
        <section class="SectionContainer-right">
          <header-creation-steps
            selected="Logic"
            :event="event"
          />
          <h2>Logic</h2>

          <!-- Screen flows        -->

          <div class="Button Logic-button">
            <div>
              <h2 class="u-floatLeft">
                Screen Flow
              </h2>
              <button
                :disabled="ui.disableScreenFlowButton"
                class="Button Button-transparent ResetFlowOrScoresButton u-floatRight"
                title="Reset all your flows."
                @click="ui.showResetFlowsModal = true"
              >
                <span class="Icon-s Icon-trash" />
              </button>
              <reset-flows
                v-model="ui.showResetFlowsModal"
                :event="event"
                @reset-flows:success="handleResetFlowsModal"
                @click="ui.showResetFlowsModal = true"
              />
            </div>
            <div class="clear">
              <p>
                Create different user journeys based on the given answers.
              </p>
            </div>

            <div>
              <button
                :disabled="ui.disableScreenFlowButton"
                class="Button Button--green-outline"
                @click="ui.showScreenFlowSettingsModal = true"
              >
                Config
              </button>
            </div>
          </div>
          <screen-flow-settings-modal
            v-model="ui.showScreenFlowSettingsModal"
            @screen-flow-settings:responseSuccess="screenFlowResponseSuccess"
          />

          <!-- Scores-->

          <div class="Button Logic-button">
            <div>
              <div class="u-floatLeft">
                <h2>Scores</h2>
                <p
                  v-if="ui.maxScore > 0"
                  class="Max-score"
                >
                  Max score per response: {{ ui.maxScore }}
                </p>
              </div>
              <div class="u-floatRight">
                <button
                  :disabled="ui.disableScoresButton"
                  class="Button Button-transparent ResetFlowOrScoresButton u-floatRight"
                  title="Reset all your scores."
                  @click="ui.showResetScoresModal = true"
                >
                  <span class="Icon-s Icon-trash" />
                </button>
                <reset-scores
                  v-model="ui.showResetScoresModal"
                  :event="event"
                  @reset-scores:success="handleResetScoresModal"
                  @click="ui.showResetScoresModal = true"
                />
              </div>
            </div>
            <div class="clear">
              <p>Set a score for each question possible answer.</p>
            </div>
            <div>
              <button
                :disabled="ui.disableScoresButton"
                class="Button Button--green-outline"
                @click="ui.showScoresSettingsModal = true"
              >
                Config
              </button>
            </div>
          </div>
          <scores-settings-modal
            v-model="ui.showScoresSettingsModal"
            @scores-settings:responseSuccess="scoresResponseSuccess"
          />

          <!-- Redirect on complete -->

          <div class="Button Logic-button">
            <h2>Redirect on completion</h2>
            <p>Define what happens when the student submits the form.</p>

            <button
              :disabled="ui.disableRedirectButton"
              class="Button Button--green-outline"
              @click="ui.showRedirectSettingsModal = true"
            >
              Config
            </button>
          </div>

          <redirect-settings-modal
            v-model="ui.showRedirectSettingsModal"
            @redirect-settings:responseSuccess="redirectResponseSuccess"
          />
        </section>
      </main>
      <footer-creation-buttons v-if="isEventCreation">
        <template
          #left-aligned-buttons
        >
          <router-link :to="{ name: 'form-builder-dynamic-form', params: { id: $route.params.id }}">
            <button
              class="Button Button--green-outline"
              :disabled="ui.isLoading"
            >
              Back
            </button>
          </router-link>
        </template>
        <template
          #right-aligned-buttons
        >
          <button
            :class="[{'is-loading': ui.submitLoading }, 'Button Button--green']"
            :disabled="ui.isLoading"
            @click="submit"
          >
            Save & Continue
          </button>
        </template>
      </footer-creation-buttons>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex';
import HeaderCreationSteps from '../common/header-creation-steps.vue';
import FooterCreationButtons from '../common/footer-creation-buttons.vue';
import DynamicFormRendererReadOnly from '../dynamic-web-forms/dynamic-form-renderer/dynamic-form-renderer-read-only';
import RedirectSettingsModal from '../modals/redirect-settings.vue';
import ScoresSettingsModal from '../modals/scores-settings.vue';
import ScreenFlowSettingsModal from '../modals/screen-flow-settings.vue';
import EventStageHeader from '../common/event-stage-header';
import ResetFlows from '../modals/reset-flows';
import ResetScores from '../modals/reset-scores';

export default {
  components: {
    EventStageHeader,
    HeaderCreationSteps,
    FooterCreationButtons,
    RedirectSettingsModal,
    DynamicFormRendererReadOnly,
    ScoresSettingsModal,
    ScreenFlowSettingsModal,
    ResetFlows,
    ResetScores,
  },
  data() {
    return {
      ui: {
        isLoading: false,
        submitLoading: false,
        showRedirectSettingsModal: false,
        showScoresSettingsModal: false,
        showScreenFlowSettingsModal: false,
        maxScore: 0,
        showScreenNavigationMenu: false,
        disableScreenFlowButton: true,
        disableScoresButton: true,
        disableRedirectButton: true,
        showResetFlowsModal: false,
        showResetScoresModal: false,
      },
    };
  },
  computed: {
    ...mapState('events', {
      event: (state) => state.event,
    }),
    ...mapState('forms', {
      screens: (state) => state.screens,
      currentScreen: (state) => state.currentScreen,
      selectedQuestion: (state) => state.selectedQuestion,
      formQuestions: (state) => state.form.questions,
    }),
    isEventCreation() {
      return this.$route.path.indexOf('create-event') > -1;
    },
  },
  mounted() {
    window.scrollTo(0, 0);
    this.setCurrentScreenNumber(1);

    if (this.$route.params.id !== undefined) {
      this.ui.isLoading = true;
      this.ui.messagesLoading = true;

      this.getEvent(this.$route.params.id)
        .then(() => {
          this.populateScreens();
          if (this.event.is_locked) {
            this.$router.push({
              name: 'event-records',
              params: { eventId: this.event.id },
            });
          } else {
            this.ui.isLoading = false;
          }
        })
        .catch(({ data }) => {
          this.ui.isLoading = false;
          this.ui.messagesLoading = false;
          this.$bus.$emit('displayMsg', {
            text: data.message,
            type: 'warning',
          });
        });
    }
  },
  methods: {
    ...mapActions('events', ['getEvent', 'updateEvent']),
    ...mapMutations('forms', ['setFormScreens', 'setCurrentScreenNumber', 'incrementCurrentFormScreen', 'decrementCurrentFormScreen']),
    submit() {
      this.$router.push({
        name: 'messages-dynamic-form',
        params: { id: this.event.id },
      });
    },
    populateScreens() {
      if (this.formQuestions.length) this.setFormScreens();
    },
    handleCloseScreenNavigationMenu() {
      this.ui.showScreenNavigationMenu = false;
    },
    toggleShowScreenMenu() {
      this.ui.showScreenNavigationMenu = !this.ui.showScreenNavigationMenu;
    },
    scoresResponseSuccess(maxScore) {
      this.ui.disableScoresButton = false;
      this.ui.maxScore = maxScore;
    },
    screenFlowResponseSuccess() {
      this.ui.disableScreenFlowButton = false;
    },
    redirectResponseSuccess() {
      this.ui.disableRedirectButton = false;
    },
    handleResetFlowsModal() {
      this.ui.showResetFlowsModal = false;
    },
    handleResetScoresModal() {
      this.ui.showResetScoresModal = false;
      this.ui.maxScore = 0;
    },
  },
};
</script>

<style lang="less" scoped>
.Logic-button {
  width: 100%;
  text-align: left;
  margin-bottom: 20px;
  cursor: default;
}

.ResetFlowOrScoresButton {
  width: 36px;
  height: 28px;
  padding: 0;
  min-width: auto;
  margin-left: 16px;
}

.Max-score {
  background-color: #e0e4ea;
  color: #848281;
  padding: 2px 4px;
  border-radius: 3px;
}

.QuestionBuilder {
  height: 100vh;
  padding: 0;
}

.SectionContainer-left {
  background: #F7F8F9 !important;
  max-height: 687px;
  overflow-y: hidden;
}

.SectionContainer-right {
  position: relative;
}

.sortable-ghost {
  box-shadow: none;
  border: 2px dotted #046FF0;
  background: transparent;
}
</style>
